import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { collection, getDocs, doc, updateDoc, deleteDoc, addDoc, query, where } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';

interface SessionData {
  id: string;
  venueId: string;
  venueName: string;
  tableId: string;
  machineId: string;
  playerId?: string;
  playerName?: string;
  startTime: string;
  endTime?: string;
  duration: number;
  matchCount: number;
  revenue: number;
  status: 'active' | 'completed' | 'paused';
}

const SessionManagement: React.FC = () => {
  const { t } = useTranslation();
  const { isAdmin, isVenueOperator, venueIds: currentUserVenueIds } = useAuth();
  const [sessions, setSessions] = useState<SessionData[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<'all' | 'active' | 'completed' | 'paused'>('all');
  const [selectedVenue, setSelectedVenue] = useState<string>('all');
  const [venues, setVenues] = useState<{ id: string; name: string }[]>([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [newSession, setNewSession] = useState({
    venueId: '',
    tableId: '',
    machineId: '',
    playerName: ''
  });

  useEffect(() => {
    loadVenues();
    loadSessions();
  }, []);

  useEffect(() => {
    loadSessions();
  }, [selectedVenue, filterStatus]);

  const loadVenues = async () => {
    try {
      const venuesSnapshot = await getDocs(collection(db, 'venues'));
      let venuesData = venuesSnapshot.docs.map(doc => ({
        id: doc.id,
        name: doc.data().name
      }));
      
      // If current user is a venue operator (not admin), filter to only show their assigned venues
      if (isVenueOperator && !isAdmin && currentUserVenueIds.length > 0) {
        venuesData = venuesData.filter(venue => currentUserVenueIds.includes(venue.id));
      }
      
      setVenues(venuesData);
    } catch (error) {
      console.error('Error loading venues:', error);
    }
  };

  const loadSessions = async () => {
    try {
      setLoading(true);
      const sessionsRef = collection(db, 'sessions');
      let sessionsQuery = query(sessionsRef);
      
      if (selectedVenue !== 'all') {
        sessionsQuery = query(sessionsRef, where('venueId', '==', selectedVenue));
      }
      
      if (filterStatus !== 'all') {
        sessionsQuery = query(sessionsQuery, where('status', '==', filterStatus));
      }
      
      const sessionsSnapshot = await getDocs(sessionsQuery);
      const sessionsData: SessionData[] = [];
      
      sessionsSnapshot.forEach((doc) => {
        sessionsData.push({
          id: doc.id,
          ...doc.data()
        } as SessionData);
      });
      
      setSessions(sessionsData.sort((a, b) => 
        new Date(b.startTime).getTime() - new Date(a.startTime).getTime()
      ));
    } catch (error) {
      console.error('Error loading sessions:', error);
    } finally {
      setLoading(false);
    }
  };

  const addSession = async () => {
    try {
      const venue = venues.find(v => v.id === newSession.venueId);
      await addDoc(collection(db, 'sessions'), {
        ...newSession,
        venueName: venue?.name || 'Unknown',
        startTime: new Date().toISOString(),
        duration: 0,
        matchCount: 0,
        revenue: 0,
        status: 'active'
      });
      setShowAddModal(false);
      setNewSession({ venueId: '', tableId: '', machineId: '', playerName: '' });
      await loadSessions();
    } catch (error) {
      console.error('Error adding session:', error);
    }
  };

  const updateSessionStatus = async (id: string, status: 'active' | 'completed' | 'paused') => {
    try {
      const updateData: any = { status };
      
      if (status === 'completed') {
        updateData.endTime = new Date().toISOString();
      }
      
      await updateDoc(doc(db, 'sessions', id), updateData);
      await loadSessions();
    } catch (error) {
      console.error('Error updating session:', error);
    }
  };

  const deleteSession = async (id: string) => {
    if (window.confirm(t('sessionManagement.confirmDelete'))) {
      try {
        await deleteDoc(doc(db, 'sessions', id));
        await loadSessions();
      } catch (error) {
        console.error('Error deleting session:', error);
      }
    }
  };

  const filteredSessions = sessions.filter(session =>
    session.tableId?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    session.machineId?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    session.playerName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    session.venueName?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatDuration = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-4 border-blue-600 mx-auto mb-4"></div>
          <p className="text-[#B8B8C3]">{t('sessionManagement.loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 pt-16 lg:pt-0 pb-16 sm:pb-0 card-dark rounded-lg shadow p-6">
      <div className="mb-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
          <div>
            <h2 className="text-4xl font-bold text-gradient-cyan mb-2">{t('sessionManagement.title')}</h2>
            <p className="text-[#B8B8C3]">{t('sessionManagement.description')}</p>
          </div>
          <button
            onClick={() => setShowAddModal(true)}
            className="hidden sm:flex px-4 py-2 bg-cyan-500 text-white rounded-lg hover:bg-cyan-600 transition-colors items-center gap-2 whitespace-nowrap"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            {t('sessionManagement.startSession')}
          </button>
        </div>
        
        <button
          onClick={() => setShowAddModal(true)}
          className="sm:hidden w-full mb-4 px-4 py-2 bg-cyan-500 text-white rounded-lg hover:bg-cyan-600 transition-colors flex items-center justify-center gap-2"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          {t('sessionManagement.startSession')}
        </button>
      </div>

      <div className="mb-6 grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="relative">
          <input
            type="text"
            placeholder={t('sessionManagement.searchPlaceholder')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full px-4 py-2 pl-10 border border-[#31383D] rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
          <svg
            className="absolute left-3 top-3 w-5 h-5 text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            />
          </svg>
        </div>
        <select
          value={selectedVenue}
          onChange={(e) => setSelectedVenue(e.target.value)}
          className="px-4 py-2 border border-[#31383D] rounded-lg focus:ring-2 focus:ring-blue-500"
        >
          <option value="all">{t('sessionManagement.allVenues')}</option>
          {venues.map(venue => (
            <option key={venue.id} value={venue.id}>{venue.name}</option>
          ))}
        </select>
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value as any)}
          className="px-4 py-2 border border-[#31383D] rounded-lg focus:ring-2 focus:ring-blue-500"
        >
          <option value="all">{t('sessionManagement.allStatuses')}</option>
          <option value="active">{t('sessionManagement.active')}</option>
          <option value="paused">{t('sessionManagement.paused')}</option>
          <option value="completed">{t('sessionManagement.completed')}</option>
        </select>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
        <div className="stat-card">
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 bg-[#2bc48a]/10 rounded-lg">
              <svg className="w-8 h-8 text-[#2bc48a]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
            </div>
          </div>
          <p className="stat-label">{t('sessionManagement.activeSessions')}</p>
          <p className="stat-number">{sessions.filter(s => s.status === 'active').length}</p>
        </div>
        <div className="stat-card">
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 bg-[#d9a514]/10 rounded-lg trophy-icon">
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
          </div>
          <p className="stat-label">{t('sessionManagement.pausedSessions')}</p>
          <p className="stat-number">{sessions.filter(s => s.status === 'paused').length}</p>
        </div>
        <div className="stat-card">
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 bg-[#00AFF2]/10 rounded-lg">
              <svg className="w-8 h-8 text-[#00AFF2]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
          </div>
          <p className="stat-label">{t('sessionManagement.completedToday')}</p>
          <p className="stat-number">
            {sessions.filter(s => 
              s.status === 'completed' && 
              new Date(s.startTime).toDateString() === new Date().toDateString()
            ).length}
          </p>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-[#31383D]">
          <thead className="bg-[#1d232a]">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-[#B8B8C3] uppercase tracking-wider">
                {t('sessionManagement.venue')}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-[#B8B8C3] uppercase tracking-wider">
                {t('sessionManagement.table')}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-[#B8B8C3] uppercase tracking-wider">
                {t('sessionManagement.machine')}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-[#B8B8C3] uppercase tracking-wider">
                {t('sessionManagement.player')}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-[#B8B8C3] uppercase tracking-wider">
                {t('sessionManagement.startTime')}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-[#B8B8C3] uppercase tracking-wider">
                {t('sessionManagement.duration')}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-[#B8B8C3] uppercase tracking-wider">
                {t('sessionManagement.matches')}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-[#B8B8C3] uppercase tracking-wider">
                {t('sessionManagement.revenue')}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-[#B8B8C3] uppercase tracking-wider">
                {t('sessionManagement.status')}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-[#B8B8C3] uppercase tracking-wider">
                {t('sessionManagement.actions')}
              </th>
            </tr>
          </thead>
          <tbody className="bg-[#15191E] divide-y divide-[#31383D]">
            {filteredSessions.length === 0 ? (
              <tr>
                <td colSpan={10} className="px-6 py-4 text-center text-[#B8B8C3]">
                  {t('sessionManagement.noSessions')}
                </td>
              </tr>
            ) : (
              filteredSessions.map((session) => (
                <tr key={session.id} className="hover:bg-[#1d232a] transition-colors">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-white">{session.venueName}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-[#B8B8C3]">{session.tableId}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-[#B8B8C3]">{session.machineId}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-[#B8B8C3]">{session.playerName || '-'}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-[#B8B8C3]">
                      {new Date(session.startTime).toLocaleString()}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-[#B8B8C3]">{formatDuration(session.duration)}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {session.matchCount}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-semibold text-green-600">${session.revenue.toFixed(2)}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {session.status === 'active' ? (
                      <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                        {t('sessionManagement.active')}
                      </span>
                    ) : session.status === 'paused' ? (
                      <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-yellow-100 text-yellow-800">
                        {t('sessionManagement.paused')}
                      </span>
                    ) : (
                      <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800">
                        {t('sessionManagement.completed')}
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    {session.status === 'active' && (
                      <>
                        <button
                          onClick={() => updateSessionStatus(session.id, 'paused')}
                          className="text-yellow-600 hover:text-yellow-900 mr-3"
                        >
                          {t('sessionManagement.pause')}
                        </button>
                        <button
                          onClick={() => updateSessionStatus(session.id, 'completed')}
                          className="text-blue-600 hover:text-blue-900 mr-3"
                        >
                          {t('sessionManagement.complete')}
                        </button>
                      </>
                    )}
                    {session.status === 'paused' && (
                      <button
                        onClick={() => updateSessionStatus(session.id, 'active')}
                        className="text-green-600 hover:text-green-900 mr-3"
                      >
                        {t('sessionManagement.resume')}
                      </button>
                    )}
                    <button
                      onClick={() => deleteSession(session.id)}
                      className="text-red-600 hover:text-red-900"
                    >
                      {t('common.delete')}
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="card-dark rounded-lg p-6 w-full max-w-md">
            <h3 className="text-xl font-bold mb-4">{t('sessionManagement.startSession')}</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('sessionManagement.venue')}
                </label>
                <select
                  value={newSession.venueId}
                  onChange={(e) => setNewSession({ ...newSession, venueId: e.target.value })}
                  className="w-full px-3 py-2 border border-[#31383D] rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">{t('sessionManagement.selectVenue')}</option>
                  {venues.map(venue => (
                    <option key={venue.id} value={venue.id}>{venue.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('sessionManagement.tableId')}
                </label>
                <input
                  type="text"
                  value={newSession.tableId}
                  onChange={(e) => setNewSession({ ...newSession, tableId: e.target.value })}
                  placeholder="Table-01"
                  className="w-full px-3 py-2 border border-[#31383D] rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('sessionManagement.machineId')}
                </label>
                <input
                  type="text"
                  value={newSession.machineId}
                  onChange={(e) => setNewSession({ ...newSession, machineId: e.target.value })}
                  placeholder="Machine-01"
                  className="w-full px-3 py-2 border border-[#31383D] rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('sessionManagement.playerName')} ({t('sessionManagement.optional')})
                </label>
                <input
                  type="text"
                  value={newSession.playerName}
                  onChange={(e) => setNewSession({ ...newSession, playerName: e.target.value })}
                  className="w-full px-3 py-2 border border-[#31383D] rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
            <div className="mt-6 flex gap-3">
              <button
                onClick={addSession}
                disabled={!newSession.venueId || !newSession.tableId || !newSession.machineId}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
              >
                {t('sessionManagement.start')}
              </button>
              <button
                onClick={() => setShowAddModal(false)}
                className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
              >
                {t('common.cancel')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SessionManagement;
